/* @flow */

import React, { useContext } from "react";
import { useData } from "crustate/react";
import { useBrowser } from "@awardit/react-use-browser";
import { HomeData, CmsBlockData } from "data";
import { StoreInfoContext } from "entrypoint/shared";
import { Helmet } from "react-helmet-async";
import { useTranslate } from "@awardit/react-use-translate";
import { formatMeta } from "helpers/get-meta";
import Wrapper from "components/Wrapper";
import Hero from "components/Hero";
import LoadingView from "components/LoadingView";
import { Offerings } from "@crossroads/shop-views";
import CategorySection from "components/CategorySection";
import useBrowserDimensions from "helpers/use-browser-dimensions";
import ProductCard from "components/ProductCard";
import { useUi } from "helpers/ui";
import { MODE } from "state/view-mode";
import { Button, ButtonLink, PromotionBlock, PromotionWrapper } from "@crossroads/ui-components";
import Arrow from "icons/arrow.svg";

import styles from "./styles.scss";

const HomeView = (): React$Node => {
  const browser = useBrowser();
  const t = useTranslate();
  const home = useData(HomeData);
  const offerings = useData(CmsBlockData);
  const { info } = useContext(StoreInfoContext);
  const { width: browserWidth } = useBrowserDimensions();
  const {
    isDesktop,
    setHamburgerOpen,
    setSubNavOpen,
    setViewMode,
    viewMode,
  } = useUi();

  const offeringsContent = offerings.state === "LOADED" && offerings.data ?
    offerings.data.content : null;

  if (home.state !== "LOADED") {
    return <LoadingView />;
  }

  const meta = formatMeta({
    title: info.defaultTitle,
    description: info.defaultDescription,
  });

  const getPopularProductAmount = () => {
    if (browserWidth < Number.parseInt(styles.tiny, 10)) {
      return 2;
    }

    if (browserWidth < Number.parseInt(styles.medium, 10)) {
      return 3;
    }

    return 4;
  };

  return (
    <div className={styles.block}>
      <Helmet
        title={meta.title}
        meta={meta.data}
      />

      <Hero
        smallImage={home.data.hero.block.smallImage}
        largeImage={home.data.hero.block.largeImage}
        modifier={home.data.hero.block.modifier}
        heading={home.data.hero.block.heading}
        subHeading={home.data.hero.block.subHeading}
        splash={home.data.hero.block.splash}
        text={home.data.hero.block.text}
        link={home.data.hero.block.link}
        CTA={home.data.hero.block.cta}
      />

      <Wrapper>
        <div className={styles.popularCategories}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.title}>{t("HOMEVIEW.POPULAR_CATEGORIES")}</h2>
            <Button
              type="button"
              aria-controls={isDesktop ? "desktopCategories" : "categoriesMobile"}
              aria-expanded={viewMode === MODE.CATEGORIES ?? "false"}
              variant="blank"
              className={styles.cta}
              onClick={() => {
                setHamburgerOpen(true);
                setSubNavOpen(true);
                setViewMode(MODE.CATEGORIES);
              }}
            >
              {t("HOMEVIEW.ALL_CATEGORIES")}
              <Arrow aria-hidden="true" />
            </Button>
          </div>

          <CategorySection />
        </div>
        {home.data.featuredProducts.category &&
        home.data.featuredProducts.category.products.items.length >= 3 && (
          <div className={styles.popularProducts}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.title}>{t("HOMEVIEW.POPULAR_PRODUCTS")}</h2>
              <ButtonLink
                variant="blank"
                className={styles.cta}
                to={home.data.featuredProducts.category.url}
              >
                {t("HOMEVIEW.EXPLORE_CATEGORY", { category: home.data.featuredProducts.category.name.toLowerCase() })}
                <Arrow aria-hidden="true" />
              </ButtonLink>
            </div>
            <div className={styles.products}>
              {browser && home.data.featuredProducts.category.products.items
                .slice(0, getPopularProductAmount())
                .map((p, i) => (
                  <div key={p.name} className={styles.product}>
                    <ProductCard product={p} position={i} list={t("HOMEVIEW.POPULAR_PRODUCTS")} />
                  </div>
                ))}
            </div>
          </div>
        )}

        {offeringsContent && (
          <div className={styles.offeringsSection}>
            <h2 className={styles.sectionHeader}>{t("HOMEVIEW.OFFERINGS")}</h2>
            <Offerings items={offeringsContent} />
          </div>
        )}

        <div className={styles.promotionSection}>
          <PromotionBlock {...home.data.block2FullWidth.block} variant="full" />

          <PromotionWrapper variant="double">
            <PromotionBlock {...home.data.block1Left.block} variant="double" />
            <PromotionBlock {...home.data.block1Right.block} variant="double" />
          </PromotionWrapper>
        </div>
      </Wrapper>
    </div>
  );
};

export default HomeView;
